import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

export default function Announcements() {
  const { token } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', active: true });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) { window.location.href = '/login'; return; }
      const data = await res.json();
      setList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingItem 
      ? `${API_URL}/admin/announcements/${editingItem.id}`
      : `${API_URL}/admin/announcements`;
    
    const method = editingItem ? 'PATCH' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData({ title: '', content: '', active: true });
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('确认删除此公告?')) return;
    try {
      await fetch(`${API_URL}/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error(error);
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ title: item.title, content: item.content, active: item.active });
    setIsModalOpen(true);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">通知公告管理</h1>
        <button 
          onClick={() => { setEditingItem(null); setFormData({ title: '', content: '', active: true }); setIsModalOpen(true); }}
          className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={18} className="mr-2" /> 发布公告
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800/50 border border-gray-100 dark:border-slate-700 rounded-xl shadow overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-100 dark:border-slate-700">
            <tr>
              <th className="p-3 text-xs font-semibold text-gray-600 dark:text-gray-300">标题</th>
              <th className="p-3 text-xs font-semibold text-gray-600 dark:text-gray-300">内容摘要</th>
              <th className="p-3 text-xs font-semibold text-gray-600 dark:text-gray-300">状态</th>
              <th className="p-3 text-xs font-semibold text-gray-600 dark:text-gray-300">创建时间</th>
              <th className="p-3 text-xs font-semibold text-gray-600 dark:text-gray-300">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {list.map(item => (
              <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 text-xs">
                <td className="p-3 font-medium text-gray-900 dark:text-gray-200">{item.title}</td>
                <td className="p-3 text-gray-600 dark:text-gray-300 truncate max-w-xs">{item.content}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-xs ${item.active ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                    {item.active ? '显示中' : '隐藏'}
                  </span>
                </td>
                <td className="p-3 text-gray-600 dark:text-gray-300 text-xs">
                  {new Date(item.createdAt).toLocaleDateString()}
                </td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => openEdit(item)} className="text-blue-600 hover:bg-blue-50 p-2 rounded">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:bg-red-50 p-2 rounded">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200 dark:border-slate-700 p-6 rounded-2xl w-full max-w-lg shadow-2xl">
            <h2 className="text-xl font-bold mb-4 dark:text-white">{editingItem ? '编辑公告' : '发布公告'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">标题</label>
                <input 
                  type="text" 
                  className="w-full border rounded-lg p-2 dark:bg-slate-900/50 dark:border-slate-600 dark:text-white" 
                  value={formData.title} 
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">内容</label>
                <textarea 
                  className="w-full border rounded-lg p-2 dark:bg-slate-900/50 dark:border-slate-600 dark:text-white h-32" 
                  value={formData.content} 
                  onChange={e => setFormData({...formData, content: e.target.value})}
                  required
                />
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="active"
                  className="mr-2"
                  checked={formData.active} 
                  onChange={e => setFormData({...formData, active: e.target.checked})}
                />
                <label htmlFor="active" className="text-sm text-gray-700 dark:text-gray-300">立即显示在首页</label>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 rounded-lg">取消</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">保存</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
